import { useEffect, useState } from "react";
import { ArrowUpRight, MapPin, Factory } from "lucide-react";
import { api } from "@/lib/api";

const PROJECT_IMG =
  "https://images.unsplash.com/photo-1709489662983-3674d790b224?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxvcGVuJTIwcGl0JTIwbWluZXxlbnwwfHx8fDE3Nzg5NzQxMTd8MA&ixlib=rb-4.1.0&q=85";

const STATUS_TAG = {
  advancing: { label: "Advancing", cls: "bb-tag-positive" },
  permitting: { label: "Permitting", cls: "bb-tag-neutral" },
  delayed: { label: "Delayed", cls: "bb-tag-negative" },
  operational: { label: "Operational", cls: "bb-tag-positive" },
};

const companyDisplay = (id, lookup) => {
  if (!id) return "—";
  const c = lookup[id];
  return c ? c.name : id;
};

const commodityDisplay = (arr) =>
  Array.isArray(arr) ? arr.join(" · ") : arr || "—";

const ProjectRow = ({ p, companyLookup }) => {
  const tag = STATUS_TAG[p.status] || STATUS_TAG.advancing;
  return (
    <tr
      className="border-b border-border last:border-b-0 hover:bg-bb-offwhite transition-colors group"
      data-testid={`project-row-${p.id}`}
    >
      <td className="p-4 align-top">
        <div className="font-archivo font-semibold text-bb-navy text-base leading-tight">
          <a href={`/projects/${p.slug}`} className="bb-link">
            {p.name}
          </a>
        </div>
        <div className="text-xs text-bb-slate mt-1 font-mono">
          {companyDisplay(p.company, companyLookup)}
        </div>
      </td>
      <td className="p-4 align-top">
        <div className="inline-flex items-center gap-1.5 text-xs font-mono text-bb-slate">
          <MapPin size={12} /> {p.province}
        </div>
      </td>
      <td className="p-4 align-top">
        <div className="inline-flex items-center gap-1.5 text-xs font-mono text-bb-slate">
          <Factory size={12} /> {commodityDisplay(p.commodity)}
        </div>
      </td>
      <td className="p-4 align-top">
        <div className="bb-overline text-bb-navy">{p.stage}</div>
      </td>
      <td className="p-4 align-top">
        <span className={`text-[11px] font-mono px-2 py-1 ${tag.cls}`}>
          {tag.label}
        </span>
      </td>
      <td className="p-4 align-top font-mono text-sm text-bb-navy">
        {p.capexEstimate ? `$${p.capexEstimate.toLocaleString()}M` : "—"}
      </td>
    </tr>
  );
};

export const ProjectWatchPreview = () => {
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    let mounted = true;
    Promise.all([api.get("/projects"), api.get("/companies")])
      .then(([pr, co]) => {
        if (!mounted) return;
        if (Array.isArray(pr.data)) setProjects(pr.data);
        if (Array.isArray(co.data)) setCompanies(co.data);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const companyLookup = companies.reduce((acc, c) => {
    acc[c.id] = c;
    return acc;
  }, {});

  if (projects.length === 0) return null;

  const featured = projects[0];
  const rows = projects.slice(0, 6);
  const featuredCompany = companyLookup[featured.company];

  return (
    <section
      id="project-watch"
      className="py-14 lg:py-20 border-b border-border bg-white"
      data-testid="project-watch-section"
    >
      <div className="bb-container">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="bb-overline text-bb-copper">Project Watch</div>
            <h2 className="font-archivo text-3xl lg:text-4xl text-bb-navy mt-2 font-bold tracking-tight">
              Where Canadian capital is moving.
            </h2>
          </div>
          <a
            href="/projects"
            className="bb-link text-xs font-archivo uppercase tracking-widest text-bb-navy inline-flex items-center gap-1"
            data-testid="project-watch-view-all"
          >
            Full project database <ArrowUpRight size={14} strokeWidth={2.4} />
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-border border border-border">
          {/* Featured project */}
          <article
            className="bg-white lg:col-span-4 flex flex-col group"
            data-testid="project-watch-featured"
          >
            <div className="relative border-b border-border overflow-hidden">
              <img
                src={PROJECT_IMG}
                alt={featured.name}
                className="bb-img w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              />
              <span className="absolute top-4 left-4 bg-bb-navy text-white font-archivo text-[10px] tracking-[0.2em] uppercase px-3 py-1.5">
                Featured Project
              </span>
            </div>
            <div className="p-6 lg:p-7 flex flex-col gap-3 flex-1">
              <div className="bb-overline text-bb-copper">
                {featured.province} · {commodityDisplay(featured.commodity)}
              </div>
              <h3 className="font-archivo text-2xl text-bb-navy font-bold tracking-tight leading-tight">
                <a href={`/projects/${featured.slug}`} className="bb-link">
                  {featured.name}
                </a>
              </h3>
              <div className="text-xs font-mono text-bb-slate">
                Operator · {featuredCompany ? featuredCompany.name : featured.company}
              </div>
              <p className="text-sm text-bb-slate leading-relaxed">
                {featured.summary}
              </p>
              <div className="mt-auto pt-4 border-t border-border grid grid-cols-2 gap-4">
                <div>
                  <div className="bb-overline text-bb-slate">Stage</div>
                  <div className="font-archivo font-semibold text-bb-navy mt-1">
                    {featured.stage}
                  </div>
                </div>
                <div>
                  <div className="bb-overline text-bb-slate">Capex</div>
                  <div className="font-mono text-bb-navy mt-1">
                    {featured.capexEstimate
                      ? `$${featured.capexEstimate.toLocaleString()}M`
                      : "—"}
                  </div>
                </div>
              </div>
            </div>
          </article>

          {/* Table */}
          <div className="bg-white lg:col-span-8 overflow-x-auto">
            <table
              className="w-full text-left"
              data-testid="project-watch-table"
            >
              <thead>
                <tr className="border-b border-border bg-bb-offwhite">
                  <th className="p-4 bb-overline text-bb-slate">Project</th>
                  <th className="p-4 bb-overline text-bb-slate">Province</th>
                  <th className="p-4 bb-overline text-bb-slate">Commodity</th>
                  <th className="p-4 bb-overline text-bb-slate">Stage</th>
                  <th className="p-4 bb-overline text-bb-slate">Status</th>
                  <th className="p-4 bb-overline text-bb-slate">Capex</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <ProjectRow p={p} companyLookup={companyLookup} key={p.id} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectWatchPreview;
